import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Toast from "src/components/toast";
import { toast } from "react-toastify";

/**
 * GlobalToastManager
 * 
 * A generic toast manager that listens to updates in React Query's cache.
 * Specifically, it subscribes to any query with data that can trigger a toast,
 * such as migration progress updates. When it detects a completed action
 * that has a `toastMeta` property and hasn't already fired a toast, it
 * automatically displays a success toast using the Toast component.
 *
 * Behavior:
 * - Subscribes to all cache updates on mount.
 * - Checks for completed actions with toastMeta.
 * - Shows the toast only once per data entry (marks `toastFired: true` after showing).
 * - Unsubscribes from cache updates on unmount.
 *
 * Usage:
 * - Place <GlobalToastManager /> at a high level in your app (e.g., App.tsx)
 *   so that it can capture global events and display toasts anywhere.
 * - Any component or hook can trigger a toast by updating the query cache
 *   with a `toastMeta` object and `completed: true`.
 */


interface ToastData {
  completed?: boolean;
  toastFired?: boolean;
  toastMeta?: {
    heading: string;
    message: string;
  };
}

export const GlobalToastManager: React.FC = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event?.type !== "updated") return;

      const data = event.query.state.data as ToastData;
      if (!data?.completed || data.toastFired || !data.toastMeta) return;

      toast(
        <Toast.Sucess
          heading={data.toastMeta.heading}
          message={data.toastMeta.message}
        />
      );

      queryClient.setQueryData(event.query.queryKey, {
        ...data,
        toastFired: true,
      });
    });

    return () => unsubscribe();
  }, [queryClient]);

  return null;
};
